/*
 * Designer Rugs — product page behaviours (loaded on the product template).
 * Relies on global.js (whenParsed, trapFocus) being loaded first.
 */

/* The section renders its own script tag, so editor re-injection can load
   this file twice — bail out if the elements are already defined.*/
if (!customElements.get('product-page')) {

/**/
/* Unit conversion for size labels (hardened item 6 — the original*/
/* Only matched "…cm"-suffixed numbers, so the toggle no-op'd on the*/
/* Real catalogue's bare formats). Reads the shared dr:units*/
/* Preference (footer + PDP toggles). Handles: "240 x 300" · "240x300"*/
/* · "250cm Diameter" / "Diameter 250cm" · construction-prefixed*/
/* "Hand Tuft 240x300" (prefix kept) · "Custom Size" and anything*/
/* Unparseable pass through untouched — never blanked. No lookbehind*/
/* (a regex-literal SyntaxError would kill the whole file on older*/
/* Safari) — leading context is captured and re-emitted instead.*/
/**/
function cmToImperial(cm) {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches - feet * 12);
  if (inches === 12) return `${feet + 1}'0"`;
  return `${feet}'${inches}"`;
}

/* Money from the shop's money_format (passed in the product JSON) —
   the trade calculator computes client-side, so it needs a formatter.
   Handles the standard tokens; unknown formats fall back to $X.*/
function formatMoney(cents, format, noDecimals, includeCurrency = true) {
  const amount = cents / 100;
  /* No trailing double-zeros anywhere — whole-
     dollar amounts drop the ".00" (mirrors Liquid's
     money_without_trailing_zeros); real cents are kept.*/
  if (cents % 100 === 0) noDecimals = true;
  const two = amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const zero = Math.round(amount).toLocaleString('en-AU');
  const fmt = format || '${{amount}}';
  const formatted = fmt
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, zero)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, zero)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, noDecimals ? zero : two)
    .replace(/\{\{\s*amount\s*\}\}/g, noDecimals ? zero : two)
    .trim();
  const suffix = includeCurrency && window.DRPriceCurrency?.enabled ? ' ' + window.DRPriceCurrency.code : '';
  return formatted + suffix;
}

function convertSizeLabel(label, unit) {
  if (unit !== 'imperial') return label;
  if (/custom/i.test(label)) return label; // Custom Size — non-dimensional
  let converted = false;
  /* 🔴 ONE PATTERN, WITH `cm` AS AN OPTIONAL PART OF THE TOKEN.
     This used to be two alternatives — "digits followed by cm" OR "a bare
     number" — and the bare-number branch captured the character BEFORE the
     number. So in "160 × 230cm" the scan matched " 230" from the preceding
     space before the cm-branch could ever be tried at "230", the `cm` was
     never consumed, and imperial rendered `5'3" × 7'7"cm`. caught it
     on the PDP. new `A × Bcm` format made it visible: with the old
     `240cm x 300cm` every number carried its own cm, which masked it.
     ⚠ It was ALSO wrong before, for diameters, but only when the closed
     state prefixes the construction: "Hand Tufted 220cm Diameter" gave
     `7'3"cm Diameter` while the bare value gave `7'3" Diameter`. Same cause.
     Now `cm` is an optional trailing group of the SAME match, so it is
     always consumed with the number it belongs to.
     ⚠ The leading class excludes ' and " so the pass cannot re-enter an
     already-emitted imperial value like 7'10" and convert its "10".
     ⚠ Still no lookbehind — a regex-literal SyntaxError would kill this
     whole file on older Safari. Leading
     context is captured and re-emitted instead.*/
  const out = label.replace(
    /(^|[^\d.'"])(\d{2,4}(?:\.\d+)?)\s*(cm)?\b/gi,
    (match, pre, num) => {
      const cm = parseFloat(num);
      // only plausible rug dimensions convert; stray numerals stay put
      if (!Number.isFinite(cm) || cm < 20 || cm > 3000) return match;
      converted = true;
      return pre + cmToImperial(cm);
    }
  );
  /* The separator follows the canon (`160 × 230cm`): a multiplication
     sign with spaces. Imperial keeps its feet/inch marks, so the unit is
     always shown and the numbers are never bare, and `cm`
     is gone because the match above consumed it.*/
  return converted ? out.replace(/\s*[x×]\s*/gi, ' × ') : label;
}


/**/
/* Product page: size/variant resolution, stock note, quantity, add*/
/* To cart, OOS swap, combined-listing child swap via the Section*/
/* Rendering API (no reload — URL/price/gallery/details all update).*/
/**/
class ProductPage extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    const json = this.querySelector('[data-product-json]');
    this.data = json ? JSON.parse(json.textContent) : { variants: [] };
    this.sizeSelect = this.querySelector('[data-size-select]');
    this.qtyInput = this.querySelector('[data-qty-input]');

    this.sizeSelect?.addEventListener('change', () => {
      const opt = this.sizeSelect.selectedOptions[0];
      /* A sibling-construction size swaps the whole CHILD product
         (gallery, material, price) via the existing no-reload mechanism*/
      if (opt?.dataset.productUrl) {
        this.swapChild(opt.dataset.productUrl);
        return;
      }
      if (this.sizeSelect.value === '__custom__') {
        /* The calculator renders on TRADE only;
           when it exists, Custom Size opens it inline.
           Everywhere else (retail always; trade with the calculator
           switched off) Custom Size opens the ENQUIRY sheet — no
           dimensions, no pricing. Last-resort fallback: the old
           custom-page navigation.*/
        if (this.calc) { this.tradeSizeChanged(); return; }
        if (this.data.trade) {
          this.tradeSizeChanged();
          const price = this.querySelector('[data-price]');
          if (price) price.textContent = this.t('custom_options');
          return;
        }
        this.reflectVariant();
        /* Custom Size carries NO variant price, so this puts the
           server-rendered "From" figure back rather than leaving the previously
           selected size's price sitting above an enquiry form.*/
        this.updateRetailPrice();
        if (this.querySelector('[data-sheet="custom-enquiry"]')) {
          this.sheets?.open('custom-enquiry', this.querySelector('.pdp-size__button') || this.sizeSelect);
          return;
        }
        const url = this.data.custom_url;
        if (url) { window.location.href = url; return; }
        return;
      }
      if (this.calc) { this.tradeSizeChanged(); return; }
      this.reflectVariant();
      /* Keep the price bound to the selected size, including first load. */
      this.updateRetailPrice();
    });

    /* Keyboard selection of a radio fires `change`,
       and in some browsers arrow-key movement does not raise a click at all.
       Binding change as well is what makes the selector work for a keyboard
       user, which treats as a launch blocker rather than polish.*/
    this.addEventListener('change', (event) => {
      const swap = event.target.closest?.('[data-child-swap]');
      if (swap?.dataset.productUrl) this.swapChild(swap.dataset.productUrl);
    });

    this.addEventListener('click', (event) => {
      const qtyBtn = event.target.closest('[data-qty-change]');
      if (qtyBtn && this.qtyInput) {
        const next = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + Number(qtyBtn.dataset.qtyChange));
        this.qtyInput.value = next;
        /* Setting .value programmatically fires NO event, so a
           screen reader was never told the quantity had changed. Announce it.
           ⚠ No upper clamp: this store reports inventory_quantity 0 on
           products that are `available: true`, i.e. inventory is not tracked,
           and sells out-of-stock as PRE-ORDER. Capping here would block
           legitimate orders — see the note in main-product.liquid.*/
        const status = this.querySelector('[data-qty-status]');
        if (status) status.textContent = `${this.t('quantity') || 'Quantity'}: ${next}`;
      }
      if (event.target.closest('[data-add-to-cart]')) this.addToCart();
      if (event.target.closest('[data-add-to-enquiry]')) this.addToEnquiry();
      const panelOpener = event.target.closest('[data-sheet-open="trade-enquiry"]');
      if (panelOpener) this.prepTradePanel(panelOpener.dataset.enquiryType || 'enquiry');
      const swap = event.target.closest('[data-child-swap]');
      if (swap) {
        /* The construction selector is now REAL
           RADIOS, not links. preventDefaulton a radio's click stops the
           browser checking it, so only an anchor gets it. swapChildguards
           re-entry, so the change handler below firing too is harmless.*/
        if (swap.tagName === 'A') event.preventDefault();
        this.swapChild(swap.dataset.productUrl);
      }
      const share = event.target.closest('[data-share]');
      if (share) this.share();
    });

    this.initTrade();

    this.onUnits = (event) => this.applyUnits(event.detail?.unit);
    document.addEventListener('units:change', this.onUnits);
    let stored = null;
    try {
      stored = localStorage.getItem('dr:units');
    } catch (e) { /* Session-only*/ }
    this.applyUnits(stored || 'metric');
    this.reflectVariant();
    this.updateRetailPrice();
  }

  disconnectedCallback() {
    document.removeEventListener('units:change', this.onUnits);
    this.initialized = false;
  }

  applyUnits(unit) {
    if (!unit) return;
    this.sizeSelect?.querySelectorAll('option[data-metric]').forEach((opt) => {
      opt.textContent = convertSizeLabel(opt.dataset.metric, unit);
    });
    // trade size radios carry their labels in a span (comp checkboxes)
    this.querySelectorAll('[data-size-label][data-metric]').forEach((el) => {
      el.textContent = convertSizeLabel(el.dataset.metric, unit);
    });
  }

  sizeValue() {
    if (this.sizeSelect) return this.sizeSelect.value;
    const checked = (this.tradeSizeInputs || []).find((input) => input.checked);
    return checked ? checked.value : null;
  }

  currentVariant() {
    const size = this.sizeValue();
    if (!size || size === '__custom__') return this.data.variants[0] || null;
    /* 🔴 NEVER SILENTLY SUBSTITUTE A DIFFERENT SIZE.
       This used to end `|| this.data.variants[0]`, so a selection that
       matched no variant fell back to the FIRST one. Combined with Liquid
       offering listing-wide sizes on a combined-listing child, that meant a
       customer could choose "190cm x 280cm" on Buttercup and add
       "220cm Diameter" to the cart — wrong goods, no warning. The Liquid
       side is fixed (sizes now come from this product's own variants), so
       this should be unreachable; it stays as a safety net, because a
       silent wrong-size substitution is the worst way for that to fail.*/
    return this.data.variants.find((v) => v.options.includes(size)) || null;
  }

  /**/
  /* Trade PDP: checkbox-style size selection, the inline*/
  /* Custom-size calculator, Add to Enquiry and the enquiry panel*/
  /* Pre-fill. All no-op on retail (no [data-trade-size] elements).*/
  /**/
  initTrade() {
    this.tradeSizeInputs = Array.from(this.querySelectorAll('[data-trade-size]'));
    this.calc = this.querySelector('[data-trade-calc]');
    /* The 3-box radios are gone; the shared select drives the calc.
       Bind whenever a calc exists, radios or not.*/
    if (!this.tradeSizeInputs.length && !this.calc) return;
    this.shapeGroup = this.querySelector('[data-trade-shapes]');
    this.tradeSizeInputs.forEach((input) => input.addEventListener('change', () => this.tradeSizeChanged()));
    this.querySelectorAll('[data-trade-shape]').forEach((input) => input.addEventListener('change', () => this.calcUpdate()));
    this.calc?.querySelectorAll('[data-calc-input]').forEach((input) => input.addEventListener('input', () => this.calcUpdate()));
    this.readVariantRates();
    this.tradeSizeChanged();
  }

  /* The m² rate lives on the VARIANT (ERP sync writes
     custom.erp_square_metre_price per variant) with the product-level
     custom.sqm_price as the fallback. Liquid resolves the rate for the
     variant it rendered; this re-resolves it when the shopper moves
     between sizes without a reload. A construction change swaps the whole
     child product, so that path re-renders and Liquid resolves again.*/
  readVariantRates() {
    if (!this.calc) return;
    /* "id:rate,id:rate" — deliberately not JSON; Shopify's live parser
       rejects a brace string literal inside a Liquid output tag.*/
    this.variantRates = {};
    (this.calc.dataset.variantRates || '').split(',').forEach((pair) => {
      const cut = pair.indexOf(':');
      if (cut < 1) return;
      const n = ProductPage.parseRate(pair.slice(cut + 1));
      if (n) this.variantRates[pair.slice(0, cut).trim()] = n;
    });
    this.fallbackRate = ProductPage.parseRate(this.calc.dataset.sqmFallback);
  }

  /* Whitespace / "$" / thousands commas / empty / non-numeric / <= 0 all
     resolve to null = ABSENT. Never 0 — a $0 rate must not price.*/
  static parseRate(raw) {
    if (raw === null || raw === undefined) return null;
    const n = parseFloat(String(raw).replace(/[$,\s]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  syncRate() {
    if (!this.calc) return;
    const variant = this.currentVariant();
    let fromVariant = variant ? this.variantRates?.[String(variant.id)] : null;
    /* Custom Size has no size variant of its own — currentVariantlands
       on variants[0], which is often the $0 "Custom Size" row. Price the
       custom piece off this design's own ERP rate wherever it sits.*/
    if (!fromVariant && this.customSelected()) {
      const first = (this.data.variants || []).find((v) => this.variantRates?.[String(v.id)]);
      if (first) fromVariant = this.variantRates[String(first.id)];
    }
    const rate = fromVariant || this.fallbackRate || null;
    this.calc.dataset.sqm = rate ? String(rate) : '';
    this.calc.dataset.sqmSource = fromVariant ? 'variant' : (rate ? 'product' : '');
    /* The UNDISCOUNTED rate element is gone (two figures only),
       so nothing writes it any more. The row itself survives because it now
       carries the label and the DISCOUNTED rate, and still hides when this
       design cannot be priced (never print a $0).*/
    const row = this.calc.querySelector('[data-calc-rate-row]');
    if (row) row.hidden = !rate;
  }

  customSelected() {
    return this.sizeValue() === '__custom__';
  }

  tradeShape() {
    const checked = Array.from(this.querySelectorAll('[data-trade-shape]')).find((input) => input.checked);
    return checked ? checked.value : 'rectangular';
  }

  shapeLabel() {
    const shape = this.tradeShape();
    return shape === 'round' ? 'Round'
      : shape === 'runner' ? 'Runner'
      : shape === 'irregular' ? 'Irregular'
      : 'Rectangular';
  }

  tradeSizeChanged() {
    const custom = this.customSelected();
    this.syncRate();
    if (this.calc) this.calc.hidden = !custom;
    if (this.shapeGroup) this.shapeGroup.hidden = !custom;
    this.tradeMessage('');
    if (custom) {
      this.calcUpdate();
    } else {
      this.reflectVariant();
      this.updateTradePrice();
    }
    /* Runs on BOTH branches: choosing Custom Size must put the
       "from" figure back, not leave the previous size's price on screen.*/
    this.updateRetailPrice();
  }

  updateTradePrice() {
    if (!this.data.trade) return;
    const priceEl = this.querySelector('[data-price]');
    const variant = this.currentVariant();
    if (!priceEl || !variant) return;
    /* Mirrors the Liquid block: the trade price is retail × (1 − tier%),
       rounded ONCE; the RRP is the retail figure, labelled, greyed, SQUARE
       BRACKETED and now the SAME point size, no strikethrough. The old line
       here printed the RAW RETAIL price as the only figure — the "which
       number is this" defect, JS half.

       🔴 THIS IS THE SECOND RENDER SITE FOR THE PRICE ROW AND IT MUST MOVE
       WITH THE LIQUID. The Liquid at sections/main-product.liquid paints the
       row on load; this repaints it on every variant, colourway and
       construction swap. Change one and the row silently reverts to the old
       treatment the moment a shopper picks a size — the client would see the
       fix on arrival and watch it undo itself. Both were changed together for
       B1.17 (31.08). */
    if (variant.price > 0) {
      const d = parseFloat(this.data.trade_discount) || 0;
      const tradeCents = Math.round(variant.price * (100 - d) / 100);
      priceEl.innerHTML =
        `<span class="pdp__price-trade">${formatMoney(tradeCents, this.data.money_format)}</span>`
        + `<span class="pdp__price-gst">${this.t('including_gst') || 'inc GST'}</span>`
        + `<span class="pdp__price-rrp">[${this.t('rrp_label') || 'RRP'} ${formatMoney(variant.price, this.data.money_format)}]</span>`;
    } else {
      priceEl.textContent = this.t('custom_options');
    }
  }

  /* RETAIL: once a size is chosen, show THAT variant's price.
     "From $X" on load and on cards; once a size is selected,
     show the variant's actual price.

     ⚠ This NARROWS 's uniform-"from" rule, it does not reverse it. The rule
     exists so a browsing shopper sees consistent pricing; once they have
     chosen a size, "from" is no longer true. CARDS ARE UNTOUCHED — 's card
     rules still say every card shows "from", and nothing here runs on a card.

     ⚠ Custom Size gets NO variant price: it opens the enquiry sheet (retail)
     or the calculator (trade), so the page falls back to the server-rendered
     "from" figure rather than pricing something that has no variant.

     ⚠ — NEVER $0. Every product carries a $0 "Custom Size" variant
     since, and a $0 once poisoned price_min catalogue-wide. A variant
     with no real price falls back to the "from" markup instead of printing
     zero.

     The original server-rendered markup is captured on first use and restored
     verbatim for the fallback, so the sale treatment, the "was" strikethrough
     and the cheapest-real-variant logic all survive untouched — none of
     that is re-implemented here.*/
  /* 🔴 229 J4 — THE SHEET'S VISIBLE PRODUCT CONTEXT IS A THIRD RENDER SITE, and
     it moves with the price. The Liquid paints it on load; this repaints it on
     every size, construction and colourway change, so opening a sheet always
     shows the CURRENT selection rather than the value stamped at page load.
     Bound to the same variant state the price row uses — not to a separate
     read — because two sources of truth is how the row and the sheet start
     disagreeing. Content only: it writes text, never a form value. */
  updateSheetContext() {
    const variant = this.currentVariant();
    this.querySelectorAll('[data-sheet-context]').forEach((ctx) => {
      const line = ctx.querySelector('[data-sheet-context-variant]');
      if (!line) return;
      const con = ctx.dataset.construction || '';
      let title = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';
      if (this.customSelected && this.customSelected()) title = this.t('custom_size') || 'Custom Size';
      let text = title;
      const flat = (x) => (x || '').toLowerCase().replace(/\s+/g, '');
      if (con && !flat(title).includes(flat(con))) {   // "Handknotted" vs "Hand Knotted" (§10 seam)
        text = title ? `${con} · ${title}` : con;
      }
      line.textContent = text;
      line.hidden = !text;            // hide-when-empty, never an empty row
      /* 231 — and the field that is EMAILED follows the same value, so the inbox
         reads the size the customer actually had selected. */
      document.querySelectorAll('[data-enquiry-selection]').forEach((f) => { f.value = text; });
      this.updateTradeAppForm({ hidden: { selection: text } });
    });
  }

  updateRetailPrice() {
    this.updateSheetContext();
    if (this.data.trade) return;                 // trade has its own path above
    const priceEl = this.querySelector('[data-price]');
    if (!priceEl) return;
    if (this.priceFromHTML === undefined) this.priceFromHTML = priceEl.innerHTML;

    const custom = this.sizeSelect && this.sizeSelect.value === '__custom__';
    const variant = custom ? null : this.currentVariant();

    if (!variant || !(variant.price > 0)) {       // includes NaN/undefined
      priceEl.innerHTML = this.priceFromHTML;
      return;
    }
    const now = formatMoney(variant.price, this.data.money_format);
    const discount = Number(variant.sale_discount_percentage || 0);
    if (this.data.sale_enabled && variant.available && variant.inventory_quantity > 0 && discount > 0 && discount < 100) {
      const salePrice = Math.round(variant.price * (100 - discount) / 100);
      const saleNow = formatMoney(salePrice, this.data.money_format, undefined, false);
      priceEl.innerHTML =
        `<span class="pdp__sale-price-row">`
        + `<span class="pdp__price-now pdp__price-now--accent">${saleNow}</span>`
        + `<span class="pdp__price-was-label">${this.t('was') || 'was'}</span>`
        + `<s class="pdp__price-was">${now}</s>`
        + `<span class="pdp__sale-tag">${discount}% ${this.t('off_rrp') || 'OFF RRP'}</span>`
        + `</span>`;
    } else {
      priceEl.textContent = now;
    }
  }

  /* The calculator: Total m² + indicative trade rate (INC-GST, no suffix —
     reverses the trade comp; the discount comes off this
     inclusive rate and RRP derives from the same number) +
     RRP, live off sqm_price × the account's tier mirror. Minimums are
     per-shape settings (trade: 4 m² rect / 2.4 m² runner / 200 cm
     round); maximums come from the product's custom_max_* metafields.
     Indicative only — never an order.*/
  calcState() {
    const calc = this.calc;
    if (!calc) return null;
    const toCm = (value, unit) => {
      const n = parseFloat(value);
      if (!Number.isFinite(n) || n <= 0) return null;
      const factor = { m: 100, mm: 0.1, in: 2.54, ft: 30.48 }[(unit || 'cm').toLowerCase()] || 1;
      return n * factor;
    };
    const shape = this.tradeShape();
    const num = (sel) => {
      const input = calc.querySelector(`[data-calc-input="${sel}"]`);
      const n = parseFloat(input?.value);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    const state = {
      shape,
      width: num('width'),
      length: num('length'),
      diameter: num('diameter'),
      sqm: ProductPage.parseRate(calc.dataset.sqm),
      discount: parseFloat(calc.dataset.discount) || 0,
      maxWidth: toCm(calc.dataset.maxWidth, calc.dataset.maxWidthUnit),
      maxLength: toCm(calc.dataset.maxLength, calc.dataset.maxLengthUnit),
      maxDiameter: toCm(calc.dataset.maxDiameter, calc.dataset.maxDiameterUnit),
      minRect: parseFloat(calc.dataset.minRect) || 0,
      minRunner: parseFloat(calc.dataset.minRunner) || 0,
      minDiameter: parseFloat(calc.dataset.minDiameter) || 0,
      /* Per-shape PER-FIELD minimums (cm), placeholder + rule*/
      minRectWidth: parseFloat(calc.dataset.minRectWidth) || 0,
      minRectLength: parseFloat(calc.dataset.minRectLength) || 0,
      minRunnerWidth: parseFloat(calc.dataset.minRunnerWidth) || 0,
      minRunnerLength: parseFloat(calc.dataset.minRunnerLength) || 0,
    };
    state.round = shape === 'round';
    /* IRREGULAR: width/length are the OVERALL SPACE the shape
       must fit; area still totals for the enquiry, but nothing prices.*/
    state.irregular = shape === 'irregular';
    state.area = state.round
      ? (Math.PI * Math.pow(state.diameter / 2, 2)) / 10000
      : (state.width * state.length) / 10000;
    state.entered = state.round ? state.diameter > 0 : state.width > 0 && state.length > 0;
    state.overMax = false;
    if (state.round) {
      if (state.maxDiameter && state.diameter > state.maxDiameter) state.overMax = true;
    } else {
      if (state.maxWidth && state.width > state.maxWidth) state.overMax = true;
      if (state.maxLength && state.length > state.maxLength) state.overMax = true;
    }
    state.belowMin = false;
    state.minKey = null;
    if (state.entered) {
      if (state.round && state.minDiameter && state.diameter < state.minDiameter) {
        state.belowMin = true;
        state.minKey = 'below_min_round';
      } else if (shape === 'runner' && state.minRunner && state.area < state.minRunner) {
        state.belowMin = true;
        state.minKey = 'below_min_runner';
      } else if (shape === 'rectangular' && state.minRect && state.area < state.minRect) {
        state.belowMin = true;
        state.minKey = 'below_min_rect';
      }
      /* Per-field minimums
         checked after the area rules so the message names the tighter
         constraint the user actually broke. Irregular has no minimums
*/
      if (!state.belowMin && shape === 'rectangular') {
        if ((state.minRectWidth && state.width < state.minRectWidth) ||
            (state.minRectLength && state.length < state.minRectLength)) {
          state.belowMin = true;
          state.minKey = 'below_min_dims';
        }
      }
      if (!state.belowMin && shape === 'runner') {
        if ((state.minRunnerWidth && state.width < state.minRunnerWidth) ||
            (state.minRunnerLength && state.length < state.minRunnerLength)) {
          state.belowMin = true;
          state.minKey = 'below_min_dims';
        }
      }
    }
    return state;
  }

  calcUpdate() {
    if (!this.calc) return;
    const state = this.calcState();
    if (!state) return;
    // rectangular/runner/irregular take Width × Length; round takes Diameter
    this.calc.querySelector('[data-calc-field="width"]').hidden = state.round;
    this.calc.querySelector('[data-calc-field="length"]').hidden = state.round;
    this.calc.querySelector('[data-calc-field="diameter"]').hidden = !state.round;

    /* The light-grey placeholder states the MINIMUM for the
       shape; fields without a known minimum keep the
       up-to placeholder.*/
    const ph = (sel, min, upTo) => {
      const input = this.calc.querySelector(`[data-calc-input="${sel}"]`);
      if (!input) return;
      input.placeholder = min ? this.t('calc_min').replace('[value]', min) : (upTo || input.placeholder);
    };
    if (state.round) {
      ph('diameter', state.minDiameter, null);
    } else if (state.shape === 'rectangular') {
      ph('width', state.minRectWidth, this.calc.dataset.phUpToWidth);
      ph('length', state.minRectLength, this.calc.dataset.phUpToLength);
    } else if (state.shape === 'runner') {
      ph('width', state.minRunnerWidth, this.calc.dataset.phUpToWidth);
      ph('length', state.minRunnerLength, this.calc.dataset.phUpToLength);
    } else {
      ph('width', 0, this.calc.dataset.phUpToWidth);
      ph('length', 0, this.calc.dataset.phUpToLength);
    }

    /* The explanatory note shows for IRREGULAR only*/
    const shapeNote = this.calc.querySelector('[data-calc-shape-note]');
    if (shapeNote) shapeNote.hidden = !state.irregular;

    const total = this.calc.querySelector('[data-calc-total]');
    /* The area waits for a dimension too. "Total: m²" is the same
       reading-of-nothing as the $0 below it, and the markup shipped it as a
       hard-coded initial value, so it was on screen before any interaction.*/
    if (total) total.textContent = state.entered ? `${state.area.toFixed(2)} m²` : '';

    /* 🔴 blanked the FIGURES
       but left the LABELS standing, so the empty state read "Total:" and
       "RRP total (inc GST)" with nothing beneath them — words pointing at
       nothing. The comp draws no empty state at all (one populated drawing),
       so the whole totals stack now WAITS for a dimension the same way its
       figures already did. The rates stack waits only for a price source:
       per-m² rates exist before any dimension and keep rendering (comp
       behaviour), but a design with no rate, or Irregular (never an
       indicative price), would otherwise show ITS labels over nothing too.
       ⚠ The CSS carries explicit [hidden]{display:none} for both — they have
       author display:grid, and any author display rule silently defeats the
       hidden attribute (burned twice).*/
    const totalsGrid = this.calc.querySelector('.pdp-calc__grid--totals');
    if (totalsGrid) totalsGrid.hidden = !(state.entered && state.sqm && !state.irregular);
    const ratesGrid = this.calc.querySelector('.pdp-calc__grid--rates');
    if (ratesGrid) ratesGrid.hidden = !(state.sqm && !state.irregular);

    const rateEl = this.calc.querySelector('[data-calc-trade-rate]');
    const totalEl = this.calc.querySelector('[data-calc-trade-total]');
    const rrpRateEl = this.calc.querySelector('[data-calc-rrp-rate]');
    const rrpTotalEl = this.calc.querySelector('[data-calc-rrp-total]');
    /* State.sqm is null (never 0/NaN) when no source can price this
       design; blank the figures rather than print a $0.
       IRREGULAR never shows an indicative price at all.

       🔴 job A2 — THE TOTAL WAS COMPUTED FROM THE UNDISCOUNTED RATE.
       The rate shown was already discounted and correct; the only total on
       the page was `area × state.sqm`, i.e. RRP, so the discounted total
       expects appeared NOWHERE. That was the whole defect.

       ⚠ ONE ROUNDING PATH. The total derives from the DISCOUNTED RATE that
       is actually displayed — never by discounting a total, and never by
       re-applying the discount to an already-discounted figure. This is
       exactly arithmetic: $1,360 × 6 = $8,160.

       ⚠ .5/(settled, unchanged): the rate is INCLUSIVE of GST
       and the discount comes off the inc-GST rate. Nothing here adds GST on
       top, and no "does not include GST" note is restored — the old site
       displays ex-GST, which is a DISPLAY difference, not an arithmetic one.*/
    /* 🔴 NO RENDERED $0, EVER. Measured live on the trade PDP:
       before any dimension is typed, `state.area` is 0, so both totals were
       printing "$0" beside their labels — a price of nothing, on the page,
       for every trade visitor who opened Custom Size and read before typing.
       The RATES are per-m² and do not depend on a dimension, so they still
       render; the TOTALS wait for `state.entered`, and until then take the
       same blank the no-rate case has always used rather than a $0 or an
       invented placeholder glyph the comp does not draw.*/
    if (state.sqm && !state.irregular && state.entered) {
      /* FOUR figures, ONE rounding path each: every total is its own
         rate × area, never a discounted total and never a discount re-applied
         to an already-discounted figure:
           RRP/m² = round(sqm × 100)
           trade/m² = round(sqm × 100 × (1 − d/100))
           RRP total = round(RRP/m² × area)
           trade total = round(trade/m² × area)
         Labels live in the markup — every value on screen carries words.*/
      const rrpCents  = Math.round(state.sqm * 100);
      const rateCents = Math.round(state.sqm * 100 * (1 - state.discount / 100));
      const clean = rateCents % 100 === 0;
      if (rrpRateEl)  rrpRateEl.textContent  = formatMoney(rrpCents, this.data.money_format, rrpCents % 100 === 0);
      if (rateEl)     rateEl.textContent     = formatMoney(rateCents, this.data.money_format, clean);
      if (rrpTotalEl) rrpTotalEl.textContent = formatMoney(Math.round(rrpCents * state.area), this.data.money_format);
      if (totalEl)    totalEl.textContent    = formatMoney(Math.round(rateCents * state.area), this.data.money_format);
    } else if (state.sqm && !state.irregular) {
      /* Rate known, nothing entered yet: the per-m² figures stand, the
         totals stay blank (see above)*/
      const rrpCents = Math.round(state.sqm * 100);
      const rateCents = Math.round(state.sqm * 100 * (1 - state.discount / 100));
      if (rrpRateEl) rrpRateEl.textContent = formatMoney(rrpCents, this.data.money_format, rrpCents % 100 === 0);
      if (rateEl) rateEl.textContent = formatMoney(rateCents, this.data.money_format, rateCents % 100 === 0);
      for (const el of [rrpTotalEl, totalEl]) if (el) el.textContent = '';
    } else {
      for (const el of [rrpRateEl, rateEl, rrpTotalEl, totalEl]) if (el) el.textContent = '';
    }

    const flag = this.calc.querySelector('[data-calc-flag]');
    if (flag) {
      let message = '';
      if (state.overMax) message = this.t('over_max');
      else if (state.belowMin) message = this.t(state.minKey);
      flag.textContent = message;
      flag.hidden = !message;
    }
  }

  tradeMessage(text) {
    const el = this.querySelector('[data-trade-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  }

  customDimsLabel() {
    const state = this.calcState();
    if (!state || !state.entered) return '';
    /* 🔴 canonical size format everywhere sizes render: `160 × 230cm`
       Round pieces keep
       the store's stored "250cm Diameter" shape.
       ⚠ THIS IS A FORK OF snippets/size-label AND IT HAS TO BE: the custom
       dimensions are typed by the customer and never exist as a stored
       variant, so there is no value for Liquid to format — the label is
       composed client-side for the Enquiry List. It is the ONE place
       allowed to build the string itself, and it must be changed in step
       with the snippet. Everything that formats a STORED size goes through
       size-label.*/
    return state.round
      ? `${state.diameter}cm Diameter`
      : `${state.width} × ${state.length}cm`;
  }

  /* An enquiry can include unavailable stock and made-to-order selections.
     It is a saved product list, never a purchase or inventory reservation. */
  async addToEnquiry() {
    if (this.pending) return;
    const custom = this.customSelected();
    const variant = this.currentVariant();
    const product = this.data.enquiry_product;
    const properties = {};
    if (product?.construction) properties.Construction = product.construction;
    if (custom) {
      const state = this.calcState();
      if (this.calc && (!state || !state.entered)) {
        this.tradeMessage('Please enter the dimensions for your custom rug.');
        this.calc.querySelector(`[data-calc-input="${state?.round ? 'diameter' : 'width'}"]`)?.focus();
        return;
      }
      if (state && (state.overMax || state.belowMin)) {
        this.calcUpdate();
        return;
      }
      properties['Custom Size'] = this.customDimsLabel() || 'Dimensions to be confirmed';
      properties.Shape = this.shapeLabel();
      if (state?.sqm && !state.irregular) {
        const rateCents = Math.round(state.sqm * 100 * (1 - state.discount / 100));
        properties['Indicative rate'] = `${formatMoney(rateCents, this.data.money_format, rateCents % 100 === 0)} p/m²`;
      }
    } else if (!variant) {
      this.tradeMessage('Please select a size to add to your enquiry.');
      return;
    }

    this.pending = true;
    const button = this.querySelector('[data-add-to-enquiry]');
    button?.setAttribute('aria-busy', 'true');
    try {
      if (!product || !window.DRTradeEnquiry?.active) throw new Error('Enquiry list not ready');
      window.DRTradeEnquiry.add({
        ...product,
        variantId: custom ? null : variant.id,
        url: product.url + (custom ? '' : '?variant=' + variant.id),
        selection: custom ? 'Custom Size' : variant.title,
        properties,
        price: custom ? null : Math.round(variant.price * (1 - (this.data.trade_discount || 0) / 100)),
        quantity: 1
      });
      document.querySelector('cart-drawer')?.open(button);
      try { window.drTrack?.('dr_add_to_enquiry', { context: 'trade', product_handle: (window.__dr || {}).handle || null }); } catch (_) {}
      this.tradeMessage('');
    } catch (_) {
      this.tradeMessage('Your enquiry could not be saved. Please allow browser storage and try again.');
    } finally {
      this.pending = false;
      button?.removeAttribute('aria-busy');
    }
  }

  /* The Book an Appointment / Make an Enquiry buttons share one panel —
     typed. Pre-fills the custom size fields from the calculator when a
     custom size is in play.*/
  prepTradePanel(type) {
    const title = this.querySelector('[data-trade-panel-title]');
    if (title) {
      const label = type === 'appointment' ? title.dataset.titleAppointment : title.dataset.titleEnquiry;
      if (label) title.textContent = label;
    }
    const enquiryType = type === 'appointment' ? 'Appointment request' : 'Product enquiry';
    const patch = { hidden: { enquiry_type: enquiryType }, enquiryType: enquiryType, prefill: {} };
    if (this.customSelected()) {
      const dims = this.customDimsLabel();
      if (dims) patch.prefill['Custom Size (Dimensions)'] = dims;
      patch.prefill['Custom Size (Shape)'] = this.shapeLabel();
    }
    this.updateTradeAppForm(patch);
  }

  /* Powerful Form Builder ids are generated implementation detail. Its hidden
     fields resolve the shared `Globo.FormBuilder.dr` object; visible values are
     handed to the label-based bridge. Keep a tiny synchronous fallback here so
     a click that beats the deferred bridge script still carries the right CTA
     type and selected variant. */
  updateTradeAppForm(patch) {
    const wrapper = this.querySelector('[data-app-placement][data-form-id="trade-enquiry"]');
    if (!wrapper || !patch) return;
    let context = wrapper.__drAppFormContext;
    if (!context) {
      const source = wrapper.querySelector('[data-dr-app-form-context]');
      try { context = source ? JSON.parse(source.textContent) : { hidden: {}, prefill: {} }; }
      catch (e) { context = { hidden: {}, prefill: {} }; }
      wrapper.__drAppFormContext = context;
    }
    context.hidden = context.hidden || {};
    context.prefill = context.prefill || {};
    if (patch.hidden) Object.assign(context.hidden, patch.hidden);
    if (patch.prefill) Object.assign(context.prefill, patch.prefill);
    if (patch.enquiryType) wrapper.dataset.enquiryType = patch.enquiryType;
    window.Globo = window.Globo || {};
    window.Globo.FormBuilder = window.Globo.FormBuilder || {};
    window.Globo.FormBuilder.dr = window.Globo.FormBuilder.dr || {};
    Object.assign(window.Globo.FormBuilder.dr, context.hidden);
    if (window.DRAppForms && typeof window.DRAppForms.update === 'function') {
      window.DRAppForms.update(wrapper, patch);
    }
  }

  reflectVariant() {
    const variant = this.currentVariant();
    if (!variant) return;
    /* With Custom Size selected (and no calculator), the cart
       CTA hands over to the custom-size enquiry button and the stock note
       goes quiet: there is no variant being bought.*/
    const custom = this.customSelected() && !this.calc;
    const note = this.querySelector('[data-stock-note]');
    if (note) {
      if (this.customSelected()) {
        note.textContent = '';
      } else if (this.data.trade) {
        const qty = Math.max(0, Number(variant.inventory_quantity) || 0);
        note.textContent = `${qty} available${!variant.available ? ' · ' + this.t('oos') : ''}`;
      } else if (!variant.available) {
        note.textContent = this.t('oos');
      } else {
        const qty = variant.inventory_quantity;
        const left = qty > 0 && qty <= 3 ? `( ${qty} ${this.t('left')} ) ` : '';
        const dispatch = this.data.shipping_note || '';
        note.textContent = `${left}${this.t('in_stock')} ${dispatch}\n${this.t('shipping')}`.trim();
      }
    }
    const atc = this.querySelector('[data-add-to-cart]');
    const oos = this.querySelector('[data-oos-enquire]');
    const customBtn = this.querySelector('[data-custom-enquire]');
    if (atc && oos) {
      atc.hidden = custom || !variant.available;
      oos.hidden = custom || variant.available;
    }
    if (customBtn) customBtn.hidden = !custom;
    this.querySelector('[data-oos-copy]')?.toggleAttribute('hidden', custom || variant.available);
  }

  t(key) {
    return this.data.strings?.[key] || '';
  }

  async addToCart() {
    const variant = this.currentVariant();
    if (!variant || !variant.available || this.pending) return;
    this.pending = true;
    const atc = this.querySelector('[data-add-to-cart]');
    atc?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${this.dataset.addUrl || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variant.id,
          quantity: Math.max(1, parseInt(this.qtyInput?.value, 10) || 1),
          sections: 'cart-drawer',
        }),
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || 'add failed');
      const drawer = document.querySelector('cart-drawer');
      const html = state.sections?.['cart-drawer'];
      // 221 J2 — in enquiry mode the drawer IS the Enquiry List sidebar; open it (Chris, 03.09, superseding 216's silent-add + message line).
      if (drawer && html) {
        drawer.renderContents(html);
        drawer.open();
      }
      // /cart/add.js returns the line item, not the cart — fetch the
      // cart for the bag count.
      const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => null);
      if (cart && drawer) drawer.syncCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:change', { detail: cart || state }));
      /* 226 — the INTERNATIONAL add. In enquiry mode this same addToCart IS the
         "Add to Enquiry" (intl.js only relabels the button, it never adds), so
         the event is chosen by the enquiry-mode flag rather than by the button. */
      try {
        if (window.drTrack) {
          const intl = document.documentElement.hasAttribute('data-enquiry-mode');
          window.drTrack(intl ? 'dr_add_to_enquiry' : 'dr_add_to_cart',
            { context: intl ? 'international' : 'retail', product_handle: (window.__dr || {}).handle || null });
        }
      } catch (e) {}
    } catch (e) {
      const note = this.querySelector('[data-stock-note]');
      if (note) note.textContent = this.t('error');
    } finally {
      this.pending = false;
      atc?.removeAttribute('aria-busy');
    }
  }

  /*
   * Combined-listing sibling swap: fetch the sibling child product's page
   * for this section only, morph the section content and update the URL
   * without a navigation (GTM-safe — history.replaceState keeps the
   * page instance; gallery, price, availability, CTAs, breadcrumb all
   * arrive from the re-rendered section).
   */
  /* Which control the focus was in, and what represents that same
     choice after the swap. Adding a swap control means adding a row here.*/
  static SWAP_FOCUS = [
    { group: '.pdp-options__row--construction', current: '.pdp-options__row--construction input[type="radio"]:checked' },
    { group: '.pdp-swatches', current: '.pdp-swatches__item.is-selected' },
    { group: '.pdp-options__values', current: '.pdp-options__value.is-selected' },
  ];

  async swapChild(productUrl) {
    if (!productUrl || this.swapping) return;
    // App mounts need document bootstrap; DOMParser swaps leave their scripts inert.
    if (this.querySelector('[data-native-retail-app]')) {
      this.swapping = true;
      const restore = (event) => {
        if (!event.persisted) return;
        this.swapping = false;
        window.removeEventListener('pageshow', restore);
      };
      window.addEventListener('pageshow', restore);
      window.location.href = productUrl;
      return;
    }
    /* 🔴 KEEP THE KEYBOARD USER WHERE THEY WERE.
       The swap ends in replaceWith, which destroys the focused element, so
       a shopper who changed an option by keyboard was silently dumped to the
       top of the document with focus on <body>.
       ⚠ EVERY child-swap control has this problem, not just construction:
       the colour SWATCHES and the colour CHIPS route through the same
       swapChild. Each entry names the group focus may have been in and the
       element in the FRESH markup that now represents the same choice.
       treats a control that strands keyboard users as a launch blocker.

       🔴 THE PROPERTY NAME MATTERS, AND MINE SILENTLY DID NOTHING. This was
       `this.focusGroup`. `focusgroup` is a real HTML attribute in current
       Chrome, so `element.focusGroup` is a REFLECTED STRING IDL property:
       assigning an object coerced it to "[object Object]" and wrote that to
       the DOM attribute — Chrome logged "focusgroup requires a recognized
       behavior token" on every swap — while the object was never stored.
       Reading it back gave a string, `.current` was undefined, and the focus
       call never ran. Prefix instance state so it cannot collide with a
       platform IDL name, including one that does not exist yet.*/
    this._swapFocus = null;
    if (document.activeElement && this.contains(document.activeElement)) {
      const found = ProductPage.SWAP_FOCUS.find((g) => document.activeElement.closest(g.group));
      if (found) this._swapFocus = found;
    }
    this.swapping = true;
    this.setAttribute('aria-busy', 'true');
    let fresh = null;
    try {
      /* Sibling-size options carry ?variant= already — join, don't
         double the query separator (a second ? corrupts both params).*/
      const sep = productUrl.includes('?') ? '&' : '?';
      const url = `${productUrl}${sep}section_id=${this.dataset.sectionId}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`swap fetch ${response.status}`);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      fresh = parsed.querySelector('product-page');
      if (!fresh) throw new Error('no product-page in swap payload');
    } catch (e) {
      // only a failed fetch/parse falls back to a real navigation
      this.swapping = false;
      this.removeAttribute('aria-busy');
      window.location.href = productUrl;
      return;
    }
    // URL first (non-fatal — a blocked replaceState must not discard the morph)
    try {
      history.replaceState({}, '', productUrl);
    } catch (e) { /* Sandboxed context; morph proceeds on the old URL*/ }
    this.swapping = false;
    this.removeAttribute('aria-busy');
    const swapFocus = this._swapFocus;
    this.replaceWith(fresh);
    if (swapFocus) {
      /* The selected swatch and chip are SPANs carrying tabindex="-1", so
         they take programmatic focus without entering the tab order*/
      fresh.querySelector(swapFocus.current)?.focus();
    }
    document.dispatchEvent(new CustomEvent('product:swapped', { detail: { url: productUrl } }));
  }

  share() {
    const payload = { title: document.title, url: window.location.href };
    if (navigator.share) {
      navigator.share(payload).catch(() => {});
    } else {
      navigator.clipboard?.writeText(payload.url);
      const note = this.querySelector('[data-stock-note]');
      if (note) note.textContent = this.t('copied');
    }
  }
}
customElements.define('product-page', ProductPage);

/**/
/* The comp's boxed size control as a real LISTBOX.*/
/* The native <select> stays in the DOM as the no-JS fallback and the*/
/* Single source of options + behaviour: every selection proxies back*/
/* Onto the select and fires its change event, so the handlers*/
/* (sibling child-swap, __custom__ inline calculator, variant*/
/* Reflection) never know the difference. Unit conversion: rows carry*/
/* [data-size-label][data-metric], so ProductPage.applyUnits rewrites*/
/* Them like any other size label.*/
class SizeListbox extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    if (this.upgraded) return;
    this.select = this.querySelector('select');
    if (!this.select) return;
    this.upgraded = true;
    this.open = false;
    const id = this.select.id || 'SizeListbox';
    const label = this.querySelector('label');

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'form-input--boxed pdp-size__button';
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.valueSpan = document.createElement('span');
    this.valueSpan.className = 'pdp-size__value';
    this.valueSpan.id = `${id}-value`;
    if (label) this.button.setAttribute('aria-labelledby', `${label.id} ${this.valueSpan.id}`);
    this.button.appendChild(this.valueSpan);

    this.list = document.createElement('ul');
    this.list.className = 'pdp-size__list';
    this.list.id = `${id}-list`;
    this.list.setAttribute('role', 'listbox');
    if (label) this.list.setAttribute('aria-labelledby', label.id);
    this.list.tabIndex = -1;
    this.list.hidden = true;
    this.button.setAttribute('aria-controls', this.list.id);

    this.classList.add('is-upgraded');
    this.appendChild(this.button);
    this.appendChild(this.list);
    this.rebuild();

    this.button.addEventListener('click', () => (this.open ? this.close() : this.show()));
    this.button.addEventListener('keydown', (event) => {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        this.show();
      }
    });
    this.list.addEventListener('keydown', (event) => this.onListKey(event));
    this.list.addEventListener('click', (event) => {
      const row = event.target.closest('[role="option"]');
      if (row) this.choose(row);
    });
    this.onDocClick = (event) => {
      if (this.open && !this.contains(event.target)) this.close(false);
    };
    document.addEventListener('click', this.onDocClick);
    this.addEventListener('focusout', (event) => {
      if (this.open && !this.contains(event.relatedTarget)) this.close(false);
    });
    // programmatic select changes (variant pre-select, applyUnits) re-reflect
    this.select.addEventListener('change', () => this.reflect());
    document.addEventListener('units:change', () => queueMicrotask(() => this.reflect()));
    this.typeBuffer = '';
    this.typeTimer = null;
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    this.upgraded = false;
  }

  /* The select's real <optgroup>s carry the
     construction group headings; render each ONCE as a non-selectable
     greyed heading with its sizes beneath and a divider after the group
     (Custom Size, the select's last bare option, lands after the final
     divider — the client's diagram). Keyboard/typeahead walk option
     rows only; headings and dividers are presentational.*/
  rebuild() {
    this.list.textContent = '';
    this.rows = [];
    const appendOption = (opt, group) => {
      if (opt.disabled) return;
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.className = 'pdp-size__option';
      li.id = `${this.list.id}-opt-${this.rows.length}`;
      li.dataset.value = opt.value;
      li.textContent = opt.textContent;
      if (group) {
        li.dataset.group = group;
        // the visible row shows the size alone; SR users still hear the
        // construction the row belongs to
        li.setAttribute('aria-label', `${group} ${opt.textContent.trim()}`);
      }
      if (opt.dataset.metric) {
        li.setAttribute('data-size-label', '');
        li.dataset.metric = opt.dataset.metric;
      }
      this.list.appendChild(li);
      this.rows.push(li);
    };
    Array.from(this.select.children).forEach((node) => {
      if (node.tagName === 'OPTGROUP') {
        const head = document.createElement('li');
        head.setAttribute('role', 'presentation');
        head.className = 'pdp-size__group';
        head.textContent = node.label;
        this.list.appendChild(head);
        Array.from(node.children).forEach((opt) => appendOption(opt, node.label));
        const divider = document.createElement('li');
        divider.setAttribute('role', 'presentation');
        divider.className = 'pdp-size__divider';
        this.list.appendChild(divider);
      } else if (node.tagName === 'OPTION') {
        appendOption(node, null);
      }
    });
    this.reflect();
  }

  reflect() {
    const sel = this.select.selectedOptions[0];
    (this.rows || []).forEach((row) => {
      row.setAttribute('aria-selected', String(!!sel && row.dataset.value === sel.value));
    });
    if (!this.valueSpan) return;
    /* Closed-state value keeps the construction visible ("Hand Knotted
       240 × 300cm") — the open list states it once as the heading, but
       two 240 × 300cm rows must stay distinguishable when closed*/
    const row = sel ? (this.rows || []).find((r) => r.dataset.value === sel.value) : null;
    const group = row ? row.dataset.group : null;
    const text = row ? row.textContent : sel ? sel.textContent : '';
    this.valueSpan.textContent = group ? `${group} ${text}` : text;
    const metric = (row && row.dataset.metric) || (sel && sel.dataset.metric);
    if (metric) {
      this.valueSpan.setAttribute('data-size-label', '');
      this.valueSpan.dataset.metric = group ? `${group} ${metric}` : metric;
    } else {
      this.valueSpan.removeAttribute('data-size-label');
      delete this.valueSpan.dataset.metric;
    }
  }

  activeRow() {
    return this.rows[this.activeIdx] || null;
  }

  setActive(idx) {
    this.activeIdx = Math.max(0, Math.min(this.rows.length - 1, idx));
    this.rows.forEach((row, i) => row.classList.toggle('is-active', i === this.activeIdx));
    const row = this.activeRow();
    if (row) {
      this.list.setAttribute('aria-activedescendant', row.id);
      row.scrollIntoView({ block: 'nearest' });
    }
  }

  show() {
    if (this.open) return;
    this.open = true;
    this.list.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    const sel = this.select.selectedOptions[0];
    const idx = sel ? this.rows.findIndex((r) => r.dataset.value === sel.value) : 0;
    this.setActive(idx >= 0 ? idx : 0);
    this.list.focus();
  }

  close(refocus = true) {
    if (!this.open) return;
    this.open = false;
    this.list.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    this.list.removeAttribute('aria-activedescendant');
    if (refocus) this.button.focus();
  }

  choose(row) {
    if (this.select.value !== row.dataset.value) {
      this.select.value = row.dataset.value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.reflect();
    this.close();
  }

  onListKey(event) {
    const { key } = event;
    if (key === 'ArrowDown') { event.preventDefault(); this.setActive(this.activeIdx + 1); return; }
    if (key === 'ArrowUp') { event.preventDefault(); this.setActive(this.activeIdx - 1); return; }
    if (key === 'Home') { event.preventDefault(); this.setActive(0); return; }
    if (key === 'End') { event.preventDefault(); this.setActive(this.rows.length - 1); return; }
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      const row = this.activeRow();
      if (row) this.choose(row);
      return;
    }
    if (key === 'Escape') { event.preventDefault(); this.close(); return; }
    if (key === 'Tab') { this.close(false); return; }
    if (key.length === 1 && key.match(/\S/)) {
      // type-ahead: match the visible label's start, 600ms buffer
      clearTimeout(this.typeTimer);
      this.typeBuffer += key.toLowerCase();
      this.typeTimer = setTimeout(() => { this.typeBuffer = ''; }, 600);
      const hit = this.rows.findIndex((r) => r.textContent.trim().toLowerCase().startsWith(this.typeBuffer));
      if (hit >= 0) this.setActive(hit);
    }
  }
}
if (!customElements.get('size-listbox')) customElements.define('size-listbox', SizeListbox);

/* The Explorug/site lightbox moved to assets/lightbox.js so it
   can load on non-product templates too (the Calendly modal on the Book
   a Consultation PAGE never upgraded while it lived here).*/


/**/
/* Gallery: thumbnails, single/grid view, zoom lightbox with arrows.*/
/**/
class ProductGallery extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.frames = Array.from(this.querySelectorAll('[data-gallery-frame]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.lightbox = this.querySelector('[data-gallery-lightbox]');
    this.stage = this.querySelector('[data-lightbox-stage]');
    this.index = 0;

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
      this.show(this.frames.findIndex((f) => f.dataset.mediaId === thumb.dataset.mediaId));
    }));
    /* The mobile pager. Same resolution by media id as the thumbs,
       so both controls drive one `show` and can never disagree.*/
    this.dots.forEach((dot) => dot.addEventListener('click', () => {
      this.show(this.frames.findIndex((f) => f.dataset.mediaId === dot.dataset.mediaId));
    }));
    this.bindSwipe();
    this.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => this.openLightbox());
    /* The gallery grid/"view all" toggle was removed; its
       handler goes with it. The zoom/lightbox binding above is untouched.*/
    if (this.lightbox) {
      this.lightbox.addEventListener('click', (event) => {
        if (event.target.closest('[data-lightbox-close]') || event.target === this.lightbox) this.closeLightbox();
        if (event.target.closest('[data-lightbox-prev]')) this.lightboxShow(this.index - 1);
        if (event.target.closest('[data-lightbox-next]')) this.lightboxShow(this.index + 1);
      });
      this.lightbox.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.closeLightbox();
        if (event.code === 'ArrowLeft') this.lightboxShow(this.index - 1);
        if (event.code === 'ArrowRight') this.lightboxShow(this.index + 1);
      });
    }
  }

  /*
   * Horizontal swipe over the gallery stage.

   * ✅ RULED AND KEPT. It was added in BEYOND the brief
   * and flagged as such; to keep it. **It is deliberate — do not
   * remove it as scope creep.** The reasoning he accepted: the ruling
   * removed the thumbnails AND the arrows AND the "swipe for more images"
   * label from mobile, leaving dots — which are a pagination INDICATOR, not
   * a mechanism. This gallery has never had swipe
   * (frames are absolutely stacked and toggled by class), so without this
   * the only way to change image on a phone would be an 8px dot.

   * ⚠ Pointer Events, not touchstart/touchend: this element also sits under
   * a mouse on desktop, and `touch*` would silently do nothing for anyone
   * testing on a trackpad. `pointerType` gates it to touch and pen so a
   * desktop drag can never page the gallery.
   * ⚠ The threshold is horizontal AND must beat the vertical delta, or a
   * normal scroll down the page pages the gallery on the way past — the
   * classic carousel-hijacks-the-scroll bug.
   */
  bindSwipe() {
    const stage = this.querySelector('.pdp-gallery__stage');
    if (!stage || this.frames.length < 2) return;
    /* 🔴 213 J1 (02.09) — THE IMAGE SLIDES WITH THE FINGER, then settles.
       p211 made the swipe register (touch-action pan-y, capture, cancel
       settles like up); the change of image was still a hard cut, because
       the frames are absolutely stacked and toggled by class. Now the drag
       moves the active frame with the finger and brings the neighbour in
       beside it; release animates both to their resting places and THEN
       flips the class, so dots, thumbnails and the (+) sync off the one
       `show()` they always used. Below the threshold the frame springs back.
       `prefers-reduced-motion`: no animated settle — the swap is instant.
       The frames keep `display: none` at rest; only the two in play are
       shown, and every inline style is cleared when the slide lands. */
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const n = this.frames.length;
    let id = null;
    let x0 = null; let y0 = null; let x1 = null; let y1 = null;
    let axis = null;        /* 'x' once the gesture is a horizontal drag */
    let neighbour = null;   /* the frame sliding in */
    let dir = 0;            /* -1 = next (finger moved left), +1 = prev */
    let animating = false;
    const wrap = (i) => ((i % n) + n) % n;
    const setX = (el, px, animate) => {
      el.style.transition = animate && !reduced ? 'transform 280ms cubic-bezier(0.22, 0.61, 0.36, 1)' : 'none';
      el.style.transform = `translateX(${px}px)`;
    };
    const clear = (el) => { if (!el) return; el.style.transition = ''; el.style.transform = ''; el.style.display = ''; el.classList.remove('is-sliding'); };
    const active = () => this.frames[this.index];
    const bring = (d) => {
      const nb = this.frames[wrap(this.index + (d < 0 ? 1 : -1))];
      if (neighbour && neighbour !== nb) clear(neighbour);
      neighbour = nb; dir = d;
      neighbour.style.display = 'block'; neighbour.classList.add('is-sliding');
    };
    const settle = () => {
      if (x0 === null) return;
      const dx = x1 - x0;
      const dy = y1 - y0;
      const w = stage.clientWidth || 1;
      const min = Math.max(24, w * 0.08);
      const go = axis === 'x' && Math.abs(dx) >= min && Math.abs(dx) > Math.abs(dy);
      id = null; x0 = null; y0 = null; x1 = null; y1 = null; axis = null;
      const cur = active();
      if (!neighbour) return;
      if (!go) { /* spring back */
        const nb = neighbour; neighbour = null;
        if (reduced) { clear(cur); clear(nb); return; }
        animating = true;
        setX(cur, 0, true); setX(nb, dir < 0 ? w : -w, true);
        const done = () => { cur.removeEventListener('transitionend', done); clear(cur); clear(nb); animating = false; };
        cur.addEventListener('transitionend', done);
        setTimeout(done, 360);
        return;
      }
      const nb = neighbour; neighbour = null;
      const target = wrap(this.index + (dir < 0 ? 1 : -1));
      const land = () => { clear(cur); clear(nb); this.show(target); animating = false; };
      if (reduced) { land(); return; }
      animating = true;
      setX(cur, dir < 0 ? -w : w, true); setX(nb, 0, true);
      const done = () => { nb.removeEventListener('transitionend', done); land(); };
      nb.addEventListener('transitionend', done);
      setTimeout(done, 360);
    };
    // Safari needs a cancellable touchmove to keep an established horizontal
    // swipe from also panning the page. Leave vertical gestures and pinch zoom
    // native; never lock the document or reset its scroll position.
    let touchOrigin = null;
    let touchAxis = null;
    stage.addEventListener('touchstart', (e) => {
      touchOrigin = e.touches.length === 1 ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
      touchAxis = null;
    }, { passive: true });
    stage.addEventListener('touchmove', (e) => {
      if (!touchOrigin || e.touches.length !== 1) return;
      const dx = Math.abs(e.touches[0].clientX - touchOrigin.x);
      const dy = Math.abs(e.touches[0].clientY - touchOrigin.y);
      if (!touchAxis && Math.max(dx, dy) >= 6) touchAxis = dx > dy ? 'x' : 'y';
      if (touchAxis === 'x' && e.cancelable) e.preventDefault();
    }, { passive: false });
    ['touchend', 'touchcancel'].forEach(type => stage.addEventListener(type, () => {
      touchOrigin = null; touchAxis = null;
    }, { passive: true }));
    stage.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' || animating) return;
      id = e.pointerId; x0 = e.clientX; y0 = e.clientY; x1 = x0; y1 = y0; axis = null;
      try { stage.setPointerCapture(e.pointerId); } catch (err) { /* capture is a nicety */ }
    }, { passive: true });
    stage.addEventListener('pointermove', (e) => {
      if (e.pointerId !== id) return;
      x1 = e.clientX; y1 = e.clientY;
      const dx = x1 - x0; const dy = y1 - y0;
      if (axis === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (axis !== 'x') return;
      const w = stage.clientWidth || 1;
      const d = dx < 0 ? -1 : 1;
      if (!neighbour || dir !== d) bring(d);
      setX(active(), dx, false);
      setX(neighbour, dx + (d < 0 ? w : -w), false);
    }, { passive: true });
    stage.addEventListener('pointerup', (e) => { if (e.pointerId !== id) return; x1 = e.clientX; y1 = e.clientY; settle(); }, { passive: true });
    stage.addEventListener('pointercancel', (e) => { if (e.pointerId !== id) return; axis = null; settle(); }, { passive: true });
  }

  show(index) {
    if (index < 0) return;
    this.index = index;
    this.frames.forEach((f, i) => f.classList.toggle('is-active', i === index));
    /* J5 — mirror the active frame's caption into the flow slot under
       the stage (hide-when-empty).*/
    const capSlot = this.querySelector('[data-gallery-caption]');
    if (capSlot) {
      const cap = (this.frames[index]?.dataset.caption || '').trim();
      capSlot.textContent = cap;
      capSlot.hidden = !cap;
    }
    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
    this.dots.forEach((d, i) => {
      d.classList.toggle('is-active', i === index);
      d.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  frameImageUrl(index) {
    const img = this.frames[index]?.querySelector('img');
    return img ? (img.currentSrc || img.src) : null;
  }

  openLightbox() {
    if (!this.lightbox) return;
    this.trigger = document.activeElement;
    this.lightboxShow(this.index);
    this.lightbox.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    window.trapFocus(this.lightbox, this.lightbox.querySelector('[data-lightbox-close]'));
  }

  lightboxShow(index) {
    const n = this.frames.length;
    if (n === 0) return;
    this.index = ((index % n) + n) % n;
    const url = this.frameImageUrl(this.index);
    if (url && this.stage) this.stage.innerHTML = `<img src="${url}" alt="">`;
    this.show(this.index);
  }

  closeLightbox() {
    this.lightbox.hidden = true;
    if (this.stage) this.stage.innerHTML = '';
    document.documentElement.style.overflow = '';
    window.removeTrapFocus(this.trigger || null);
  }
}
customElements.define('product-gallery', ProductGallery);

/* Side sheets (Book a Consultation / Make an Enquiry): the SideSheets
   class moved to global.js in main-article's enquire panel needs
   the same mechanics off the product template. Do NOT re-declare it
   here;
   the inits below resolve to the global.*/

whenParsed(() => {
  document.querySelectorAll('product-page').forEach((el) => {
    if (!el.sheets) el.sheets = new SideSheets(el);
  });
});
document.addEventListener('product:swapped', () => {
  document.querySelectorAll('product-page').forEach((el) => {
    if (!el.sheets) el.sheets = new SideSheets(el);
  });
});
}
